/**
 * Browser file-download helpers for imperative (non-TanStack-Query) blob
 * downloads that must flow through an authenticated Ky client — reading the
 * response as a Blob and driving a synthetic anchor click, so bearer /
 * subsidiary headers are applied (a bare `<a href>` or `window.open` can't send
 * them). Shared across analytics CSV/DOCX exports.
 */

/**
 * Parse `Content-Disposition` for a `filename=` (or RFC 5987
 * `filename*=UTF-8''...`) token. Returns `null` when the header is absent or
 * unparseable so callers can fall back to a deterministic local name.
 */
export function extractDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  // `filename*=UTF-8''<percent-encoded>` wins when present — servers send it for non-ASCII names.
  const star = /filename\*\s*=\s*([^']+)''([^;]+)/i.exec(header);
  const starName = star?.[2];
  if (starName) {
    try {
      return decodeURIComponent(starName);
    } catch {
      // Fall through to the unquoted variant.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * Start a download from an already-authorised `url` by clicking a transient
 * anchor — for signed URLs (e.g. GCS export links) the browser fetches directly,
 * so there is no Blob to materialise and no object URL to revoke.
 *
 * Preferred over `window.open`, which pops a tab that popup blockers can eat.
 * What actually forces the save dialog is the `Content-Disposition: attachment`
 * signed into the URL by the issuing service; the `download` attribute below is
 * ignored cross-origin and is only a same-origin fallback.
 */
export function triggerUrlDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Materialise a Blob as a file download via a hidden anchor click. The object
 * URL is revoked on the next macrotask so the browser has time to start the
 * download before the URL is freed (revoking synchronously aborts it in some
 * browsers).
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
